import { useQuery } from 'react-query';
import { useState, useEffect } from 'react';
import {
  Form,
  Input,
  Radio,
  Select,
  Tooltip,
  Checkbox,
  ColorPicker,
  FormInstance,
} from 'design-system';

import DepartmentTreeSelect from 'src/components/department-tree-select';

import './templateForm.css';
import { queryKeys } from '../../../constants/queryKeys';
import { DepartmentItem } from '../../../types/category';
import { getAllDepartment } from '../../../services/category';
import {
  Template,
  ColumnSetting,
  reportTypeMap,
  getSystemTemplates,
} from '../../../services/template';

const { TextArea } = Input;
export interface FormValues {
  baseObjective: string;
  systemTemplateId?: string;
  templateName: string;
  applicationProject: string;
  reportType: string;
  departments: string[];
  description?: string;
  enabled: boolean;
  highlightColor?: string;
  systemTemplates?: Template[];
  displayRows?: Array<{ key: string; title: string }>;
}

interface TemplateFormProps {
  onFinish: (values: FormValues, status?: 'Draft' | 'Published') => void;
  form?: FormInstance;
  selectedRowId?: string | null;
  onRowColorChange?: (color: string) => void;
  onTemplateChange?: (columnSettings?: ColumnSetting[]) => void;
  currentReportType?: string; // 新增：当前模板的实际reportType
  onReportTypeChange?: (reportType: string) => void; // 新增：reportType变化时的回调
  rowSettings?: Array<{
    rowId: number | string;
    subjectId?: string;
    displayRows: Array<{ key: string; title: string }>;
    [key: string]: unknown;
  }>; // 新增：行设置数据
  setRowDisplayRows?: React.Dispatch<
    React.SetStateAction<Record<string, Array<{ key: string; title: string }>>>
  >; // 新增：设置行显示行数的函数
}
const CustomDivider = ({ title }: { title: string }) => (
  <div className="custom-divider">
    <div className="divider-title">
      <div className="divider-indicator" />
      <span className="divider-text">{title}</span>
    </div>
    <div className="divider-line" />
  </div>
);
const TemplateOption = ({
  value,
  label,
  selected,
  onClick,
}: {
  value: string;
  label: string;
  selected: boolean;
  onClick: () => void;
}) => (
  <button
    type="button"
    className={`template-option ${selected ? 'selected' : ''}`}
    onClick={onClick}
    aria-label={`选择${label}模板`}
    aria-pressed={selected}
  >
    <div className="template-icon">
      {value === 'amoeba' && '阿'}
      {value === 'yearly' && '年'}
      {value === 'quarterly' && '季'}
      {value === 'monthly' && '月'}
    </div>
    <div className="template-label">{label}</div>
  </button>
);
export default function TemplateForm({
  onFinish,
  form,
  selectedRowId,
  onRowColorChange,
  onTemplateChange,
  currentReportType,
  onReportTypeChange,
  rowSettings,
  setRowDisplayRows,
}: TemplateFormProps) {
  const [internalForm] = Form.useForm();
  const formInstance = form || internalForm;
  const currentHighlightColor = Form.useWatch('highlightColor', formInstance);
  const currentBaseObjective = Form.useWatch('baseObjective', formInstance);

  // 使用本地状态管理displayRows，不再依赖Form.Item
  const [localDisplayRows, setLocalDisplayRows] = useState<Array<{ key: string; title: string }>>(
    []
  );

  // 监听localDisplayRows变化，同步到表单
  useEffect(() => {
    if (Array.isArray(localDisplayRows)) {
      formInstance.setFieldValue('displayRows', localDisplayRows);
    }
  }, [localDisplayRows, formInstance]);

  // 监听selectedRowId变化，当选中行时从rowSettings中获取对应的displayRows并设置到本地状态
  useEffect(() => {
    if (!selectedRowId) {
      setLocalDisplayRows([]);
      return;
    }

    if (setRowDisplayRows) {
      // 通过回调函数获取当前的rowDisplayRows状态
      setRowDisplayRows((prev) => {
        const existingDisplayRows = prev[selectedRowId];

        if (existingDisplayRows && Array.isArray(existingDisplayRows)) {
          setLocalDisplayRows(existingDisplayRows);
          return prev; // 不修改状态，只是读取
        }

        // 如果rowDisplayRows中没有，再从rowSettings中获取
        let finalSelectedRow = null;
        if (rowSettings && rowSettings.length > 0) {
          // 首先按 rowId 查找
          finalSelectedRow = rowSettings.find(
            (row) => String(row.rowId) === String(selectedRowId) || row.rowId === selectedRowId
          );

          // 如果没找到，再按 subjectId 查找
          if (!finalSelectedRow) {
            finalSelectedRow = rowSettings.find((row) => row.subjectId === selectedRowId);
          }
        }

        if (
          finalSelectedRow &&
          finalSelectedRow.displayRows &&
          finalSelectedRow.displayRows.length > 0
        ) {
          setLocalDisplayRows(
            Array.isArray(finalSelectedRow.displayRows) ? finalSelectedRow.displayRows : []
          );
        } else {
          const latestRowSettingsJson = sessionStorage.getItem('latestRowSettings');
          if (latestRowSettingsJson) {
            try {
              const latestRowSettings = JSON.parse(latestRowSettingsJson);

              const settingFromStorage = latestRowSettings.find(
                (row: { subjectId: string; displayRows: Array<{ key: string; title: string }> }) =>
                  row.subjectId === selectedRowId
              );

              if (settingFromStorage && settingFromStorage.displayRows) {
                setLocalDisplayRows(
                  Array.isArray(settingFromStorage.displayRows)
                    ? settingFromStorage.displayRows
                    : []
                );
                return prev;
              }
            } catch (e) {
              // 解析sessionStorage中的rowSettings失败，忽略错误
            }
          }

          if (currentReportType === 'KPI') {
            const defaultDisplayRows = [
              { key: 'annualPlan', title: '年度计划' },
              { key: 'actual', title: '实际' },
            ];
            setLocalDisplayRows(defaultDisplayRows);

            // 立即更新父组件的状态
            return {
              ...prev,
              [selectedRowId]: defaultDisplayRows,
            };
          }
          setLocalDisplayRows([]);
        }

        return prev;
      });
    }
  }, [selectedRowId, rowSettings, currentReportType, setRowDisplayRows]);

  // 当selectedRowId改变时，将当前localDisplayRows保存到父组件的状态中
  useEffect(() => {
    if (setRowDisplayRows && selectedRowId) {
      // 只有当localDisplayRows不是空数组时才更新
      if (
        localDisplayRows.length > 0 ||
        // 或者如果当前行在rowDisplayRows中已经有记录，我们也需要更新
        (rowSettings &&
          rowSettings.some(
            (row) =>
              (String(row.rowId) === String(selectedRowId) ||
                row.rowId === selectedRowId ||
                row.subjectId === selectedRowId) &&
              row.displayRows &&
              row.displayRows.length > 0
          ))
      ) {
        setRowDisplayRows((prev) => {
          const newState = {
            ...prev,
            [selectedRowId]: localDisplayRows,
          };
          return newState;
        });
      }
    }
  }, [selectedRowId, localDisplayRows, setRowDisplayRows, rowSettings]);

  const {
    data: systemTemplates,
    isLoading: isSystemTemplatesLoading,
    error,
  } = useQuery({
    queryKey: [queryKeys.getSystemTemplates],
    queryFn: () => getSystemTemplates(),
    staleTime: 1000 * 60 * 60,
    onError: (err: unknown) => {},
  });
  const { data: departmentList, isLoading: isDepartmentLoading } = useQuery({
    queryKey: [queryKeys.getAllDepartment],
    queryFn: () => getAllDepartment(),
    staleTime: 1000 * 60 * 60,
  });
  const departmentOptions =
    departmentList?.map((dept: DepartmentItem) => ({
      value: dept.id.toString(),
      label: dept.name,
    })) || [];
  const colorOptions = ['#fcffca', '#ffe8c3', '#adedcb', '#ffdbca', '#dfedff'];
  const templateOptions = systemTemplates
    ? systemTemplates.map((template: Template) => ({
        value: template.id,
        label: template.name,
        frequencyType: template.frequencyType,
        source: template.source,
        columnSettings: template.columnSettings,
        id: template.id,
        reportType: template.reportType,
        type: template.type,
      }))
    : [];
  const applicationProjectOptions = [{ value: 'BusinessPlan', label: '目标' }];
  const reportTypeOptions = [
    { value: 'Yearly', label: '年度报表' },
    { value: 'Quarterly', label: '季度报表' },
    { value: 'Monthly', label: '月度报表' },
  ];
  const handleColorChange = (color: string) => {
    formInstance.setFieldValue('highlightColor', color);
    if (onRowColorChange && selectedRowId) {
      onRowColorChange(color);
    }
  };
  const handleTemplateChange = (value: string) => {
    const selectedTemplate = templateOptions.find((option) => option.value === value);
    if (selectedTemplate) {
      formInstance.setFieldsValue({
        reportType: selectedTemplate.frequencyType,
        applicationProject: selectedTemplate.source,
        systemTemplateId: selectedTemplate.id,
        systemTemplates: templateOptions,
      });
      if (onTemplateChange) {
        onTemplateChange(selectedTemplate.columnSettings);
      }
    }
  };

  // 监听currentReportType变化，直接更新表单字段
  useEffect(() => {
    if (currentReportType && reportTypeMap[currentReportType]) {
      const newLabel = reportTypeMap[currentReportType];
      formInstance.setFieldValue('applicationProject', newLabel);
    } else if (currentBaseObjective && templateOptions.length > 0) {
      const selectedTemplate = templateOptions.find(
        (option) => option.value === currentBaseObjective
      );
      if (selectedTemplate && selectedTemplate.reportType) {
        const newLabel = reportTypeMap[selectedTemplate.reportType] || '目标';
        formInstance.setFieldValue('applicationProject', newLabel);
      }
    }
  }, [currentReportType, currentBaseObjective, templateOptions, formInstance]);

  // 在systemTemplates加载完成时立即设置到表单中
  useEffect(() => {
    if (systemTemplates && systemTemplates.length > 0) {
      formInstance.setFieldValue('systemTemplates', templateOptions);
    }
  }, [systemTemplates, templateOptions, formInstance]);

  // 监听baseObjective变化，当选择KPI模板且有选中行时，自动设置默认的displayRows
  useEffect(() => {
    if (!selectedRowId || !currentBaseObjective || !templateOptions.length) {
      return;
    }

    // 从选中的模板获取reportType
    const selectedTemplate = templateOptions.find(
      (option) => option.value === currentBaseObjective
    );

    if (!selectedTemplate || !selectedTemplate.reportType) {
      return;
    }

    // 通知父组件reportType变化
    if (onReportTypeChange && selectedTemplate.reportType) {
      onReportTypeChange(selectedTemplate.reportType);
    }

    if (selectedTemplate && selectedTemplate.reportType === 'KPI') {
      // 检查当前行是否已经有displayRows设置
      let hasExistingDisplayRows = false;

      if (rowSettings && rowSettings.length > 0) {
        const existingRow = rowSettings.find(
          (row) =>
            String(row.rowId) === String(selectedRowId) ||
            row.rowId === selectedRowId ||
            row.subjectId === selectedRowId
        );
        if (existingRow && existingRow.displayRows && existingRow.displayRows.length > 0) {
          hasExistingDisplayRows = true;
        }
      }

      if (!hasExistingDisplayRows && localDisplayRows.length === 0) {
        const defaultDisplayRows = [
          { key: 'annualPlan', title: '年度计划' },
          { key: 'actual', title: '实际' },
        ];

        setLocalDisplayRows(defaultDisplayRows);

        // 立即更新父组件的状态
        if (setRowDisplayRows) {
          setRowDisplayRows((prev) => ({
            ...prev,
            [selectedRowId]: defaultDisplayRows,
          }));
        }

        // 同时更新表单字段
        formInstance.setFieldValue('displayRows', defaultDisplayRows);
      }
    }
  }, [
    currentBaseObjective,
    selectedRowId,
    templateOptions,
    rowSettings,
    localDisplayRows,
    setRowDisplayRows,
  ]);

  // 获取当前选中模板的reportType对应的显示名称
  const getCurrentApplicationProjectLabel = () => {
    // 优先使用传入的currentReportType（编辑模式下使用）
    if (currentReportType && reportTypeMap[currentReportType]) {
      return reportTypeMap[currentReportType];
    }

    // 其次从选中的基础模板获取
    const currentBaseObjective = formInstance.getFieldValue('baseObjective');
    const selectedTemplate = templateOptions.find(
      (option) => option.value === currentBaseObjective
    );

    if (selectedTemplate && selectedTemplate.reportType) {
      return reportTypeMap[selectedTemplate.reportType] || '目标';
    }

    return '目标';
  };
  return (
    <div className="template-form-container">
      <div className="template-form-wrapper">
        <Form
          form={formInstance}
          id="templateForm"
          layout="horizontal"
          onFinish={(values) => {
            // 确保displayRows字段有正确的值
            if (
              currentReportType === 'KPI' &&
              selectedRowId &&
              localDisplayRows &&
              localDisplayRows.length > 0
            ) {
              values.displayRows = localDisplayRows;
              // 同时设置到表单实例中
              formInstance.setFieldValue('displayRows', localDisplayRows);
            }

            // 存储systemTemplates到form，方便后续使用
            formInstance.setFieldValue('systemTemplates', templateOptions);
            // 调用传递给TemplateForm的onFinish函数
            onFinish(values);
          }}
          initialValues={{
            enabled: true,
            // baseObjective: '' // 由父组件传递，自动兼容id
          }}
          validateTrigger={['onSubmit']}
          labelCol={{ span: 7 }}
          wrapperCol={{ span: 17 }}
          size="middle"
          colon={false}
          className="compact-form"
        >
          <CustomDivider title="模板设置" />
          <Form.Item
            name="baseObjective"
            label="基础模板"
            rules={[{ required: true, message: '请选择基础模板' }]}
          >
            <Select
              placeholder={isSystemTemplatesLoading ? '加载中...' : '请选择模板'}
              options={templateOptions}
              loading={isSystemTemplatesLoading}
              onChange={handleTemplateChange}
              optionLabelProp="label"
            />
          </Form.Item>
          <Form.Item name="systemTemplateId" style={{ display: 'none' }}>
            <Input />
          </Form.Item>
          <Form.Item name="reportType" style={{ display: 'none' }}>
            <Input />
          </Form.Item>
          <Form.Item
            name="templateName"
            label="模版名称"
            rules={[{ required: true, message: '请输入模版名称' }]}
          >
            <Input placeholder="请输入" />
          </Form.Item>
          <Form.Item
            name="applicationProject"
            label="应用项目"
            rules={[{ required: true, message: '请选择应用项目' }]}
          >
            <Input placeholder="请选择基础模板" disabled style={{ backgroundColor: '#f5f5f5' }} />
          </Form.Item>
          <Form.Item
            name="reportType"
            label="报表类型"
            rules={[{ required: true, message: '请选择报表类型' }]}
          >
            <Select placeholder="请选择" options={reportTypeOptions} disabled />
          </Form.Item>
          <Form.Item
            name="departments"
            label="作用部门"
            rules={[{ required: true, message: '请选择作用部门' }]}
          >
            <DepartmentTreeSelect
              mode="multiple"
              placeholder="请选择部门（可多选）"
              loading={isDepartmentLoading}
              options={departmentOptions}
              showSearch
            />
          </Form.Item>
          <Form.Item name="description" label="模版说明">
            <TextArea rows={4} placeholder="请输入内容" />
          </Form.Item>
          <Form.Item name="enabled" label="是否启用">
            <Radio.Group>
              <Radio value>是</Radio>
              <Radio value={false}>否</Radio>
            </Radio.Group>
          </Form.Item>
          <CustomDivider title="表格设置" />
          <Form.Item
            name="highlightColor"
            label="高亮设置"
            // help={selectedRowId ? "已选择一行，点击下方颜色应用" : "请先点击表格中的行"}
            tooltip="用于设置选中行的背景颜色"
          >
            <div>
              <div className="color-picker-container">
                <Input
                  placeholder="#FFFFFF"
                  style={{ width: 'calc(100% - 40px)' }}
                  value={currentHighlightColor || ''}
                  onChange={(e) => handleColorChange(e.target.value)}
                  size="middle"
                  disabled={!selectedRowId}
                />
                <div className="color-picker-wrapper">
                  <ColorPicker
                    value={currentHighlightColor || '#FFFFFF'}
                    onChange={(color) => handleColorChange(color.toHexString())}
                    disabled={!selectedRowId}
                  />
                </div>
              </div>
              <div className="color-presets">
                {colorOptions.map((colorOption) => (
                  <Tooltip key={colorOption} title={colorOption}>
                    <button
                      type="button"
                      onClick={() => handleColorChange(colorOption)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          handleColorChange(colorOption);
                        }
                      }}
                      aria-label={`选择颜色 ${colorOption}`}
                      className={`color-preset ${currentHighlightColor === colorOption ? 'selected' : ''}`}
                      style={{ background: colorOption }}
                      disabled={!selectedRowId}
                    />
                  </Tooltip>
                ))}
              </div>
            </div>
          </Form.Item>

          {/* KPI模板的指标项目设置 */}
          {(() => currentReportType === 'KPI')() &&
            (selectedRowId ? (
              <Form.Item label="指标项目" tooltip="用于设置选中行的核心指标年度计划表指标项目">
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                    paddingTop: '6px',
                  }}
                >
                  {[
                    { key: 'annualPlan', title: '年度计划' },
                    { key: 'actual', title: '实际' },
                    { key: 'lastMonthPlan', title: '上月遗留计划' },
                    { key: 'totalRollingPlan', title: '总滚动计划' },
                    { key: 'difference', title: '差额' },
                  ].map((item) => (
                    <div
                      key={item.key}
                      style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}
                    >
                      <Checkbox
                        checked={(() => {
                          const displayRows = localDisplayRows || [];
                          // 确保displayRows是数组
                          const displayRowsArray = Array.isArray(displayRows) ? displayRows : [];
                          const isChecked = !!displayRowsArray.some(
                            (row: { key: string; title: string }) => row.key === item.key
                          );

                          return isChecked;
                        })()}
                        onChange={(e) => {
                          // 确保currentRows是数组
                          let currentRows = localDisplayRows || [];
                          if (!Array.isArray(currentRows)) {
                            currentRows = [];
                          }

                          if (e.target.checked) {
                            // 添加 - 即使当前为空数组也能添加
                            const newRows = [...currentRows, { key: item.key, title: item.title }];
                            setLocalDisplayRows(newRows);
                            // 立即更新父组件的状态
                            if (setRowDisplayRows && selectedRowId) {
                              setRowDisplayRows((prev) => {
                                const newState = {
                                  ...prev,
                                  [selectedRowId]: newRows,
                                };
                                return newState;
                              });
                            }
                          } else {
                            // 移除 - 确保不会因为数组为空而导致问题
                            const newRows = currentRows.filter(
                              (row: { key: string; title: string }) => row.key !== item.key
                            );
                            setLocalDisplayRows(newRows);
                            // 立即更新父组件的状态
                            if (setRowDisplayRows && selectedRowId) {
                              setRowDisplayRows((prev) => {
                                const newState = {
                                  ...prev,
                                  [selectedRowId]: newRows,
                                };
                                return newState;
                              });
                            }
                          }
                        }}
                      >
                        {item.title}
                      </Checkbox>
                    </div>
                  ))}
                </div>
              </Form.Item>
            ) : (
              <div style={{ color: '#aaa', padding: '8px 0 8px 24px' }}>
                请先选择表格中的行，再设置指标项目
              </div>
            ))}
          <Form.Item name="displayRows" style={{ display: 'none' }}>
            <Input />
          </Form.Item>
          <Form.Item name="systemTemplates" style={{ display: 'none' }}>
            <Input />
          </Form.Item>
        </Form>
      </div>
    </div>
  );
}
